import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { isValidBooleanValue, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsFormInputMixin from '../../mixins/ids-form-input-mixin/ids-form-input-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsLabelStateMixin from '../../mixins/ids-label-state-mixin/ids-label-state-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsElement from '../../core/ids-element';

import '../ids-text/ids-text';

import styles from './ids-switch.scss';

const Base = IdsLocaleMixin(
  IdsFormInputMixin(
    IdsLabelStateMixin(
      IdsLabelPositionMixin(
        IdsEventsMixin(
          IdsElement
        )
      )
    )
  )
);

/**
 * IDS Switch Component
 * @type {IdsSwitch}
 * @inherits IdsElement
 * @mixes IdsFormInputMixin
 * @mixes IdsEventsMixin
 * @mixes IdsLabelStateMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsLabelPositionMixin
 * @part checkbox - the checkbox input element
 * @part slider - the sliding part of the switch
 * @part label - the label text
 */
@customElement('ids-switch')
@scss(styles)
export default class IdsSwitch extends Base {
  /**
   * Call the constructor and then initialize
   */
  constructor() {
    super();
  }

  isFormComponent = true;

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.CHECKED,
      attributes.COMPACT,
      attributes.DISABLED,
      attributes.ID,
      attributes.LABEL,
      attributes.NO_MARGINS,
      attributes.SIZE,
      attributes.VALUE
    ];
  }

  /**
   * Set the no margins state
   * @param {boolean | string} val whether to remove margins
   */
  set noMargins(val: boolean | string) {
    const hasNoMargins = stringToBool(val);
    this.toggleAttribute(attributes.NO_MARGINS, hasNoMargins);
    this.container?.classList.toggle('no-margins', hasNoMargins);
  }

  /**
   * Get the no margins state
   * @returns {boolean | string} true if margins are removed
   */
  get noMargins(): boolean | string {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Custom Element `connectedCallback` implementation
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();
  }

  /**
   * Custom Element `disconnectedCallback` implementation
   * @returns {void}
   */
  disconnectedCallback() {
    Base.prototype.disconnectedCallback.apply(this);
    this.#attachSwitchChangeEvent('remove');
    this.attachNativeEvents('remove');
  }

  /**
   * Handle changes to the id attribute
   * @param {string} value The new id value
   */
  onIdChange(value: string) {
    if (this.shadowRoot) {
      const templateId = value && value !== 'none' ? value : '';
      const label = this.shadowRoot.querySelector('label');
      const input = this.shadowRoot.querySelector('input');

      if (label && input) {
        const internalId = templateId ? `${templateId}-internal` : '';
        label.setAttribute('for', internalId);
        input.id = internalId;
      }
    }
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const disabled = stringToBool(this.disabled) ? ' disabled' : '';
    const checked = stringToBool(this.checked) ? ' checked' : '';
    const value = checked ? ` value="${this.value}"` : '';
    const rootClass = ` class="ids-switch${disabled}"`;
    const checkboxClass = ` class="checkbox"`;
    const templateId = this.id && this.id !== 'none' ? this.id : '';

    return `
      <div${rootClass}>
        <label for="${templateId}-internal">
          <input type="checkbox" id="${templateId}-internal" tabindex="0" part="checkbox" ${checkboxClass}${disabled}${checked}${value}>
          <span class="slider${checked}" part="slider"></span>
          <ids-text class="label-text" part="label">${this.label}</ids-text>
        </label>
      </div>
    `;
  }

  /**
   * Handle switch change event
   * @private
   * @param {string} option If 'remove', will remove attached events
   * @returns {void}
   */
  #attachSwitchChangeEvent(option = '') {
    if (this.input) {
      const eventName = 'change';
      if (option === 'remove') {
        const handler = this.handledEvents?.get(eventName);
        if (handler && handler.target === this.input) {
          this.offEvent(eventName, this.input);
        }
      } else {
        this.onEvent(eventName, this.input, () => {
          this.checked = !!this.input?.checked;
        });
      }
    }
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @param {string} option If 'remove', will remove attached events
   * @returns {object} The object for chaining.
   */
  attachNativeEvents(option = '') {
    if (this.input) {
      const events = ['focus', 'keydown', 'keypress', 'keyup', 'click', 'dbclick'];
      events.forEach((evt: string) => {
        if (option === 'remove') {
          const handler = this.handledEvents?.get(evt);
          if (handler && handler.target === this.input) {
            this.offEvent(evt, this.input);
          }
        } else {
          this.onEvent(evt, this.input, (e: any) => {
            this.input?.removeAttribute(attributes.VALUE);
            /**
             * Trigger event on parent and compose the args
             * will fire nativeEvents.
             * @private
             * @param {object} elem Actual event
             * @param {string} value The updated input element value
             */
            this.triggerEvent(e.type, this, {
              detail: {
                elem: this,
                nativeEvent: e,
                value: this.value,
                checked: this.input?.checked
              }
            });
          });
        }
      });
    }
    return this;
  }

  /**
   * Handle events
   * @private
   * @returns {void}
   */
  #attachEventHandlers() {
    this.#attachSwitchChangeEvent();
    this.attachNativeEvents();
  }

  /**
   * Sets the checked state to true or false
   * @param {boolean | string} value If true will set `checked` attribute
   */
  set checked(value: boolean | string) {
    const slider = this.shadowRoot?.querySelector('.slider');
    const val = stringToBool(value);

    if (val) {
      this.setAttribute(attributes.CHECKED, val.toString());
      slider?.classList.add(attributes.CHECKED);
      if (this.input) {
        this.input.setAttribute(attributes.CHECKED, val.toString());
        this.input.checked = true;
      }
    } else {
      this.removeAttribute(attributes.CHECKED);
      slider?.classList.remove(attributes.CHECKED);
      if (this.input) {
        this.input.removeAttribute(attributes.CHECKED);
        this.input.checked = false;
      }
    }
  }

  /**
   * Get the checked state
   * @returns {boolean | string} true if the switch is checked
   */
  get checked(): boolean | string {
    return stringToBool(this.input ? this.input.checked : this.getAttribute(attributes.CHECKED));
  }

  /**
   * @readonly
   * @returns {HTMLInputElement} the inner `input` element
   * @see IdsFormInputMixin.formInput
   */
  get formInput(): HTMLInputElement | null {
    return this.input ?? null;
  }

  /**
   * @readonly
   * @returns {HTMLInputElement} the inner `input` element
   */
  get input(): HTMLInputElement | null {
    return this.shadowRoot?.querySelector<HTMLInputElement>('input[type="checkbox"]') ?? null;
  }

  /**
   * Get the label element
   * @returns {HTMLLabelElement | null} the label element
   */
  get labelEl(): HTMLLabelElement | null {
    return this.shadowRoot?.querySelector('label') ?? null;
  }

  /**
   * Sets checkbox to disabled
   * @param {boolean | string} value If true will set `disabled` attribute
   */
  set disabled(value: boolean | string) {
    const val = stringToBool(value);

    if (val) {
      this.setAttribute(attributes.DISABLED, val.toString());
      this.input?.setAttribute(attributes.DISABLED, '');
      this.container?.classList.add(attributes.DISABLED);
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.input?.removeAttribute(attributes.DISABLED);
      this.container?.classList.remove(attributes.DISABLED);
    }
  }

  /**
   * Get the disabled state
   * @returns {boolean | string} true if the switch is disabled
   */
  get disabled(): boolean | string { return this.hasAttribute(attributes.DISABLED); }

  /**
   * Set the `label` text
   * @param {string | null} value of the `label` text property
   */
  set label(value: string | null) {
    const labelText = this.shadowRoot?.querySelector('.label-text') || document.createElement('span');
    if (value) {
      this.setAttribute(attributes.LABEL, value);
      labelText.innerHTML = value;
      return;
    }
    this.removeAttribute(attributes.LABEL);
    labelText.innerHTML = '';
  }

  /**
   * Get the label text
   * @returns {string} the label text
   */
  get label(): string { return this.getAttribute(attributes.LABEL) || ''; }

  /**
   * React to attributes changing on the web-component
   * @param {string} name The property name
   * @param {string} oldValue The property old value
   * @param {string} newValue The property new value
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    const isValidBool = isValidBooleanValue(newValue);

    if (!isValidBool && [attributes.VALUE].includes(name)) return;

    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;

    if (name === attributes.CHECKED) {
      this.checked = !!newValue;
    }
  }

  /**
   * Sets the checkbox `value` attribute
   * @param {string} value the value property
   */
  set value(value: string) {
    // Skip setting when set to non-boolean value
    if (!isValidBooleanValue(value) || value === '' || value === 'on') {
      return;
    }

    super.value = stringToBool(value) ? 'on' : '';
    this.checked = stringToBool(value);
  }

  /**
   * Gets the checkbox `value` attribute
   * @returns {string} the value property
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox
   */
  get value(): string { return this.checked ? (this.getAttribute(attributes.VALUE) || 'on') : ''; }

  /**
   * Set the compact mode
   * @param {boolean | string} val whether to use compact mode
   */
  set compact(val: boolean | string) {
    const isCompact = stringToBool(val);
    this.toggleAttribute(attributes.COMPACT, isCompact);
    this.container?.classList.toggle('compact', isCompact);
  }

  /**
   * Get the compact mode state
   * @returns {boolean  | string} true if compact mode is enabled
   */
  get compact(): boolean | string {
    return stringToBool(this.getAttribute(attributes.COMPACT));
  }

  /**
   * Set the size (width) of the switch
   * @param {string|null} value [xs, sm, mm, md, lg, full]
   */
  set size(value: string | null) {
    if (value) {
      this.setAttribute(attributes.SIZE, value);
    } else {
      this.removeAttribute(attributes.SIZE);
    }
  }

  /**
   * size attribute
   * @returns {string|null} size param
   */
  get size(): string | null { return this.getAttribute(attributes.SIZE); }

  /**
   * Overrides the standard "focus" behavior to instead pass focus to the inner HTMLInput element.
   */
  focus() {
    this.input?.focus();
  }
}
